import { WorkflowState } from './guided-workflow.types';

/** A saved workflow older than this is not offered for resume. */
export const WORKFLOW_STATE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const STAGES = new Set(['upload', 'card', 'remediation']);

export function workflowStateKey(userId: string | number | null | undefined, backend: string): string {
  let host = 'local';
  try { host = new URL(backend).host || host; } catch { /* relative backend */ }
  return `prep.guidedWorkflow.${userId === null || userId === undefined ? 'anon' : userId}@${host}`;
}

export function parseWorkflowState(raw: string | null, now: number): WorkflowState | null {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Partial<WorkflowState> | null;
    if (!s || typeof s !== 'object' || !STAGES.has(s.stage as string)) return null;
    const at = typeof s.at === 'number' ? s.at : 0;
    if (now - at > WORKFLOW_STATE_MAX_AGE_MS) return null;
    return {
      stage: s.stage as WorkflowState['stage'],
      path: s.path === 'new' || s.path === 'sample' ? s.path : null,
      fileId: typeof s.fileId === 'string' || typeof s.fileId === 'number' ? s.fileId : null,
      fileName: typeof s.fileName === 'string' ? s.fileName : '',
      at,
    };
  } catch {
    return null;
  }
}

export function readWorkflowState(key: string, now = Date.now()): WorkflowState | null {
  try { return parseWorkflowState(localStorage.getItem(key), now); } catch { return null; }
}

export function writeWorkflowState(key: string, state: WorkflowState | null): void {
  try {
    if (state) localStorage.setItem(key, JSON.stringify(state));
    else localStorage.removeItem(key);
  } catch { /* quota / privacy mode */ }
}

/** A saved state worth offering: a file is already uploaded. */
export function isResumable(state: WorkflowState | null): state is WorkflowState {
  return !!state && state.stage !== 'upload';
}
